import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { ShieldCheck, ShieldAlert, Target } from 'lucide-react';
import { MITRE_TACTICS } from '../mitre-data';

function getTacticColor(detections) {
  if (detections >= 40) return '#ef4444';
  if (detections >= 25) return '#f97316';
  if (detections >= 15) return '#eab308';
  return '#34d399';
}

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const tactic = MITRE_TACTICS.find(t => t.name === label);
    return (
      <div className="mitre-tooltip">
        <p className="mitre-tooltip__id">{tactic?.id}</p>
        <p className="mitre-tooltip__name">{label}</p>
        <div className="mitre-tooltip__row"><span>Detections</span><strong>{payload[0].value}</strong></div>
        <div className="mitre-tooltip__row"><span>Techniques</span><strong>{tactic?.techniques} observed</strong></div>
        <div className="mitre-tooltip__row"><span>Coverage</span><strong style={{color: tactic?.covered ? '#34d399' : '#ef4444'}}>{tactic?.covered ? 'Active' : 'Gap'}</strong></div>
      </div>
    );
  }
  return null;
};

export default function MitreCoverage() {
  const [view, setView] = useState('bar'); // 'bar' | 'grid'
  const covered   = MITRE_TACTICS.filter(t => t.covered).length;
  const gapCount  = MITRE_TACTICS.length - covered;
  const totalDet  = MITRE_TACTICS.reduce((s, t) => s + t.detections, 0);

  return (
    <div className="mitre-coverage">
      <div className="mitre-coverage__header">
        <div>
          <h3 className="mitre-coverage__title">
            <Target size={16} /> MITRE ATT&amp;CK Tactic Coverage
          </h3>
          <p className="mitre-coverage__subtitle">Detections mapped across all 12 enterprise tactics · {totalDet} total detections</p>
        </div>
        <div className="mitre-coverage__summary">
          <span className="mitre-cov-chip mitre-cov-chip--ok">
            <ShieldCheck size={11} /> {covered} Covered
          </span>
          <span className="mitre-cov-chip mitre-cov-chip--gap">
            <ShieldAlert size={11} /> {gapCount} Gaps
          </span>
          <div className="mitre-view-toggle">
            <button className={`mitre-view-btn${view === 'bar'  ? ' mitre-view-btn--active' : ''}`} onClick={() => setView('bar')}>Chart</button>
            <button className={`mitre-view-btn${view === 'grid' ? ' mitre-view-btn--active' : ''}`} onClick={() => setView('grid')}>Grid</button>
          </div>
        </div>
      </div>

      {view === 'bar' ? (
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={MITRE_TACTICS} margin={{ top: 8, right: 8, left: -10, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 11, fill: '#94a3b8' }}
              angle={-38}
              textAnchor="end"
              interval={0}
              tickLine={false}
              axisLine={false}
            />
            <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
            <Bar dataKey="detections" radius={[4, 4, 0, 0]} maxBarSize={36}>
              {MITRE_TACTICS.map((t) => (
                <Cell key={t.id} fill={getTacticColor(t.detections)} opacity={t.covered ? 1 : 0.45} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div className="mitre-grid">
          {MITRE_TACTICS.map((t) => (
            <div key={t.id} className={`mitre-tile${t.covered ? '' : ' mitre-tile--gap'}`} style={{ '--tcolor': getTacticColor(t.detections) }}>
              <div className="mitre-tile__id">{t.id}</div>
              <div className="mitre-tile__name">{t.name}</div>
              <div className="mitre-tile__count" style={{ color: getTacticColor(t.detections) }}>{t.detections}</div>
              {!t.covered && <span className="mitre-tile__gap-label">Gap</span>}
            </div>
          ))}
        </div>
      )}

      <div className="mitre-coverage__legend">
        <span className="mitre-legend-item"><span className="mitre-legend-dot" style={{background:'#34d399'}}/>1–14</span>
        <span className="mitre-legend-item"><span className="mitre-legend-dot" style={{background:'#eab308'}}/>15–24</span>
        <span className="mitre-legend-item"><span className="mitre-legend-dot" style={{background:'#f97316'}}/>25–39</span>
        <span className="mitre-legend-item"><span className="mitre-legend-dot" style={{background:'#ef4444'}}/>40+</span>
        <span className="mitre-legend-item"><span className="mitre-legend-dot" style={{background:'#64748b', opacity:0.5}}/>No coverage</span>
      </div>
    </div>
  );
}
